import React from 'react'

export default function LoadingSpinner({ fullScreen = false, size = 'default', message = '' }) {
  const sizeClasses = {
    small: 'w-8 h-8',
    default: 'w-20 h-20',
    large: 'w-32 h-32'
  }

  const containerClasses = fullScreen
    ? 'fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-blue-50 to-blue-50 z-50'
    : 'flex flex-col items-center justify-center p-8'

  return (
    <div className={containerClasses}>
      {/* Animated Logo/Spinner */}
      <div className="relative flex items-center justify-center">
        {/* Outer rotating ring */}
        <div className={`${sizeClasses[size]} relative`}>
          {/* Gradient ring 1 */}
          <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-blue-500 border-r-blue-500 animate-spin"></div>
          
          {/* Gradient ring 2 - counter rotate */}
          <div className="absolute inset-2 rounded-full border-[3px] border-transparent border-b-blue-500 border-l-pink-500 animate-spin-reverse"></div>
          
          {/* Inner pulsing circle */}
          <div className="absolute inset-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 animate-pulse shadow-lg"></div>
          
          {/* Center dot */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2.5 h-2.5 bg-white rounded-full shadow-md animate-ping"></div>
          </div>
        </div>

        {/* Floating particles */}
        <div className="absolute -inset-6">
          <div className="absolute top-0 left-1/2 w-1.5 h-1.5 bg-blue-400 rounded-full animate-float opacity-60"></div>
          <div className="absolute bottom-0 right-0 w-1.5 h-1.5 bg-blue-400 rounded-full animate-float-delayed opacity-60"></div>
          <div className="absolute top-1/2 right-0 w-1.5 h-1.5 bg-blue-400 rounded-full animate-float-slow opacity-60"></div>
        </div>
      </div>

      {/* Loading text */}
      {message && (
        <p className="mt-8 text-gray-700 font-medium text-base animate-pulse">
          {message}
        </p>
      )}
      
      {/* Animated dots */}
      {!message && (
        <div className="mt-8 flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      )}

      {/* Shimmer effect overlay */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
      </div>
    </div>
  )
}

// Skeleton Loader for content
export function SkeletonLoader({ className = '' }) {
  return (
    <div className={`animate-pulse ${className}`}>
      <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-shimmer-fast"></div>
    </div>
  )
}

// Card skeleton
export function CardSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-md p-6 animate-pulse">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-1/2"></div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
        <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-5/6"></div>
      </div>
    </div>
  )
}

// Table skeleton
export function TableSkeleton({ rows = 5 }) {
  return (
    <div className="space-y-3">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="flex items-center gap-4 animate-pulse" style={{ animationDelay: `${i * 0.1}s` }}>
          <div className="w-10 h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-full"></div>
            <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-3/4"></div>
          </div>
          <div className="w-20 h-8 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
        </div>
      ))}
    </div>
  )
}
